import RenderManager from "../rendering/render-manager";

import SlideManager from "../utils/slide-manager";

import BackgroundLayer from "./background/backgroundLayer.js";
import BackgroundComposer from "./background/backgroundComposer.js";
import AssetLibrary from "./asset-library";

const dataUriRegex = /^data:(.*?)(;base64)?,/;
class Q3Background {
    constructor(domElement) {
        const size = RenderManager.getSize();

        this.width = size.width;
        this.height = size.height;

        this.webglRootObject = new THREE.Group();
        this.webglRootObject.isBackground = true;

        this.backgroundType = "none";

        this._isUpdating = false;

        this._layers = [-2, -1].map(renderOrder => {
            let layer = new BackgroundLayer(domElement, this.webglRootObject);
            layer.setRenderOrder(renderOrder);
            return layer;
        });

        this._composer = new BackgroundComposer(this._layers);
        this._primaryLayerId = 1;

        this._visible = true; // only works with panorama for now
    }

    get _secondaryLayerId() { return (this._primaryLayerId + 1) % this._layers.length; }
    get _primaryLayer() { return this._layers[this._primaryLayerId]; }
    get _secondaryLayer() { return this._layers[this._secondaryLayerId]; }

    _flipLayers() { this._primaryLayerId = this._secondaryLayerId; }

    get isUpdating() { return this._isUpdating; }

    reloadBackground() {
        const slide = SlideManager.currentSlide;
        switch (slide.background.type) {
            case "image": {
                const isMobile = Q3.editMode ? Q3.mobileView : Q3.browserCapabilities.isMobile;
                const bgValue = isMobile && slide.background.value.image.textureMobile || slide.background.value.image.texture;

                this._updateImageBackground({
                    texture: bgValue,
                    offset: slide.background.value.image.offset,
                    scale: slide.background.value.image.scale
                }, slide.background.value.fitMode);
            } break;
        }
    }

    _updateNoBackground() {
        this._primaryLayer.setBackground({ type: "none" });
        this._composer.setLayerType(this._primaryLayerId, "none");
    }

    _updateColorBackground({ primary = "#0000ff", secondary = "", gradient = {} } = {}) {
        this._primaryLayer.setBackground({
            type: "color",
            color1: primary,
            color2: secondary,
            gradientEnabled: gradient.enabled,
            gradientType: gradient.style,
        });

        this._composer.setLayerType(this._primaryLayerId, "color");
    }

    async _getLocalBlob(textureId) {
        const texture = AssetLibrary.textures[textureId];

        if (!texture) return null;

        const dataUriRegexResult = texture.image.src.match(dataUriRegex);

        if (dataUriRegexResult && !!dataUriRegexResult[2]) {
            return {
                local: texture.image.src,
                width: texture.image.naturalWidth,
                height: texture.image.naturalHeight
            };
        } else {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            canvas.width = texture.image.naturalWidth;
            canvas.height = texture.image.naturalHeight;

            ctx.drawImage(texture.image, 0, 0);

            const b64 = canvas.toDataURL();

            texture.image.src = b64;

            return {
                local: b64,
                width: texture.image.naturalWidth,
                height: texture.image.naturalHeight
            };
        }
    }

    async _getTexture(textureId) {
        const texture = AssetLibrary.textures[textureId];

        if (!texture || !texture.isTexture) return null;

        return {
            local: texture.image.src,
            width: texture.image.naturalWidth,
            height: texture.image.naturalHeight
        };
    }



    async _updateVideoBackground(videoId, fitMode = 'cover') {
        const layer = this._primaryLayer;

        if (!(videoId in Q3.data.assets.textures)) {
            layer.setBackground({
                type: "none",
                image: "transparent"
            });

            this._composer.setLayerType(this._primaryLayerId, "image");

            return;
        }

        /**
         * @type {HTMLVideoElement}
         */
        let video = AssetLibrary.textures[videoId];

        if (!video || video.isTexture) {
            video = document.createElement("video");

            const source = document.createElement("source");
            let src = `${Q3.s3Url}${Q3.data.assets.textures[videoId].path}`;
            if (Q3.browserCapabilities.isSafari && Q3.data.assets.textures[videoId].hasMp4) {
                src = src.replace(".webm", ".mp4");
            }
            source.src = src;

            video.autoplay = true;
            video.loop = true;
            video.controls = false;
            video.muted = true;
            video.appendChild(source);

            await new Promise(resolve => video.addEventListener("loadedmetadata", () => resolve()));

            video.width = video.videoWidth;
            video.height = video.videoHeight;

            AssetLibrary.textures[videoId] = video;
        } else {
            video.pause();
            video.currentTime = 0;
        }

        await video.play();

        layer.setBackground({ type: "video", image: video, fitMode });

        this._composer.setLayerType(this._primaryLayerId, "image");
    }

    update() {
        if (this.backgroundType === "video")
            this._primaryLayer.update();
    }

    async _updateImageBackground({ texture: textureId, offset = {}, scale = {} }, fitMode = 'cover') {
        const blob = await this._getTexture(textureId);
        const layer = this._primaryLayer;
        const texture = AssetLibrary.textures[textureId];
        const image = texture ? texture.image : null;

        layer.setBackground({
            type: blob ? "image" : "none",
            image,
            imageOffsetX: offset.x,
            imageOffsetY: offset.y,
            imageScaleX: scale.x,
            imageScaleY: scale.y,
            fitMode,
        });

        this._composer.setLayerType(this._primaryLayerId, "image");
    }

    async _updatePanoramaBackground({ texture: textureId }) {
        const blob = await this._getTexture(textureId);
        const layer = this._primaryLayer;
        let texture = AssetLibrary.textures[textureId];

        if (!(texture && !texture.isCubeTexture)) texture = null;

        layer.setBackground({
            type: blob && texture ? "panorama" : "none",
            texture,
        });

        this._composer.setLayerType(this._primaryLayerId, "panorama");
    }

    setBackground({
        type = "none",
        value: { color, image, video, fitMode } = {},
        tweenTime = 1.5,
        preview = false,
    }) {
        if (typeof (arguments[0]) !== "object" || arguments.length > 1)
            throw new Error("'setBackground' API changed.");

        tweenTime = (Q3.editMode && !preview ? 0.01 : tweenTime);

        if (type === "texture") type = "panorama";
        if (this.tween && this._isUpdating) this.tween.kill();

        this._isUpdating = true;

        this._flipLayers();
        this._primaryLayer.setBackground({ type: "none" });
        this._composer.setLayerType(this._primaryLayerId, "none");

        const tweenObject = { primaryOpacity: 0 };

        const onUpdate = () => {
            let opacities = [];
            opacities[this._primaryLayerId] = tweenObject.primaryOpacity;
            opacities[this._secondaryLayerId] = 1 - tweenObject.primaryOpacity;
            this._composer.setWeights(opacities);
        };

        const onComplete = () => {
            this.finishTween();
            RenderManager.activeCamera.update = true;
            this.tween = null;
        };

        switch (type) {
            case "none": this._updateNoBackground(); break;
            case "color": this._updateColorBackground(color); break;
            case "image": this._updateImageBackground(image, fitMode); break;
            case "video": this._updateVideoBackground(video, fitMode); break;
            case "panorama": this._updatePanoramaBackground(image); break;
        }

        const tweenFrom = { primaryOpacity: 0 };
        const tweenTo = { primaryOpacity: 1, onComplete };

        this.tween = TweenMax.fromTo(tweenObject, tweenTime, tweenFrom, tweenTo);

        if (this.tween) {
            this.tween.eventCallback("onUpdate", function () {
                RenderManager.activeCamera.update = true
                onUpdate();
            });
        }

        onUpdate();

        this.backgroundType = type;
    };

    setSize(width, height) {
        this.width = width;
        this.height = height;
    }

    updateSize() {
        for (let layer of this._layers)
            layer.updateSize();
    }

    finishTween() {
        this._secondaryLayer.setBackground({ type: "none" });
        this._composer.setLayerType(this._secondaryLayerId, "none");
        this._isUpdating = false;
    }

    updateTextures() { }

    set visible(enabled) { this._visible = enabled; }
    get visible() { return this._visible; }
}

export default Q3Background;
export { Q3Background };
